/**
 * The header's combined search (agora#216): one field, Cmd/Ctrl+K or `/` to
 * focus it, a panel below grouped People/Mail/Calendar, arrows to move a
 * highlighted row without leaving the field, Enter to open it.
 *
 * **A search result is not a `Command`.** `@wtfalch/design`'s own `Command`
 * is exactly this field-over-listbox pattern already (its own doc explains
 * `aria-activedescendant` and substring filtering in the same terms this file
 * uses), but a `Command`'s items are actions with an `onRun` -- agora#216's
 * own 2026-09-21 comment calls this out directly: "a search result is not an
 * action... that is a design decision." This hand-rolls the same
 * `aria-activedescendant` combobox discipline over `SearchRow`s instead.
 *
 * Virtual focus throughout: real DOM focus never leaves the `<input>`. Each
 * option is a `<div role="option">`, not a button -- `onMouseDown` on it is
 * `preventDefault`ed so a click never blurs the field before its own
 * `onClick` runs.
 */

import type { CalendarClient } from '@wtfalch/calendar/calendar';
import { Icon, type IconName, Input, Kbd } from '@wtfalch/design';
import type { MailClient } from '@wtfalch/email/mailbox';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { FilesSource } from '../files/source.ts';
import {
  type AgoraRoute,
  parse,
  pathForRoute,
  pathForSection,
  type Route,
  type Section,
} from '../routes.ts';
import { formatMessageWhen, formatWhen } from '../ui/format.ts';
import type { ChatSearchClient } from './chat.ts';
import { createSearchController, type SearchController, type SearchState } from './controller.ts';
import { runSearch } from './model.ts';
import './search.css';
import type { SearchGroup, SearchRow } from './types.ts';

export type SearchFieldClients = {
  mail: MailClient | null;
  calendar: CalendarClient | null;
  chat: ChatSearchClient | null;
  files: FilesSource | null;
};

const GROUP_ICON: Record<string, IconName> = {
  people: 'person',
  mail: 'mail',
  calendar: 'calendar',
  chat: 'chat',
  files: 'file',
};

function placeholderFor(section: Section, companyName: string): string {
  switch (section) {
    case 'today':
      return `Search ${companyName}`;
    case 'mail':
      return 'Search mail, people and rooms';
    case 'chat':
      return 'Search chat, mail and people';
    case 'calendar':
      return 'Search events and people';
    case 'files':
      return 'Search files';
    case 'tasks':
      return 'Search tasks, mail and rooms';
  }
}

/** Where Enter, with nothing highlighted, takes this section (agora#216's
 *  2026-09-21 comment; agora#269 builds it): the full-results view at
 *  `/h/<agoraId>/<section>/search/<query>` -- `MailSearchResults`,
 *  `CalendarSearchResults` or `ChatSearchResults`, mounted by that
 *  section's own place (`Mail.tsx`, `Calendar.tsx`) or, for Chat, by
 *  `App.tsx` (`ChatSearchResults.tsx`'s own doc explains why); Files draws
 *  its own the same way Mail and Calendar do (`Files.tsx`'s own `query`
 *  check). Today has no results view of its own -- it aggregates the other
 *  four rather than answering a query itself -- so this returns null and
 *  Enter opens the first result instead (`onKeyDown` below). */
function fullSearchRoute(section: Section, agoraId: string, query: string): AgoraRoute | null {
  switch (section) {
    case 'mail':
      return {
        kind: 'agora',
        agoraId,
        section: 'mail',
        location: { mailboxId: null, threadId: null, query },
      };
    case 'calendar':
      return {
        kind: 'agora',
        agoraId,
        section: 'calendar',
        location: { date: null, eventId: null, query },
      };
    case 'chat':
      return {
        kind: 'agora',
        agoraId,
        section: 'chat',
        location: { roomId: null, threadId: null, eventId: null, query },
      };
    case 'files':
      return { kind: 'agora', agoraId, section: 'files', location: { tab: 'mine', query } };
    case 'today':
    case 'tasks':
      return null;
  }
}

/** Whether `route` is already this section's own full-results view --
 *  `setQuery` below reads this off the live address bar (not a prop) so
 *  clearing the field can send a results page back to its section's plain
 *  root without this field otherwise having to track which page it is
 *  drawn over. */
function isResultsRoute(route: Route, section: Section): boolean {
  if (route.kind !== 'agora' || route.section !== section) return false;
  if (route.section === 'today' || route.section === 'tasks') return false;
  return route.location.query !== undefined;
}

const EMPTY_STATE: SearchState<SearchGroup> = { query: '', groups: [], loading: false };

export function SearchField({
  agoraId,
  section,
  companyName,
  clients,
  go,
}: {
  agoraId: string;
  section: Section;
  companyName: string;
  clients: SearchFieldClients;
  go: (path: string) => void;
}) {
  // Read off refs inside the controller's own runner, not captured at
  // creation: the controller is built once (below) and outlives a company
  // switch or a client that only exists once sign-in finishes -- see this
  // file's own `useRef(createSearchController(...))`.
  const agoraIdRef = useRef(agoraId);
  agoraIdRef.current = agoraId;
  const clientsRef = useRef(clients);
  clientsRef.current = clients;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [state, setState] = useState<SearchState<SearchGroup>>(EMPTY_STATE);
  const [panelOpen, setPanelOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<number | null>(null);

  const controllerRef = useRef<SearchController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = createSearchController<SearchGroup>(
      (query, signal) =>
        runSearch(
          agoraIdRef.current,
          {
            mail: clientsRef.current.mail,
            calendar: clientsRef.current.calendar,
            chat: clientsRef.current.chat,
            files: clientsRef.current.files,
          },
          query,
          signal,
        ),
      setState,
    );
  }
  useEffect(() => {
    const controller = controllerRef.current;
    return () => controller?.dispose();
  }, []);

  const inputRef = useRef<HTMLInputElement>(null);
  const panelId = useId();

  const { flatRows, indexOf } = useMemo(() => {
    const flat: { group: SearchGroup; row: SearchRow }[] = [];
    const idx = new Map<string, number>();
    for (const group of state.groups) {
      if (!group.result.ok) continue;
      for (const row of group.result.rows) {
        idx.set(`${group.id}:${row.id}`, flat.length);
        flat.push({ group, row });
      }
    }
    return { flatRows: flat, indexOf: idx };
  }, [state.groups]);

  // A fresh set of results highlights nothing: a row is highlighted only by
  // the arrow keys or the pointer, so Enter on a query just typed reaches the
  // full-results view instead of always opening the first hit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `flatRows` is the trigger, not a value read.
  useEffect(() => {
    setHighlighted(null);
  }, [flatRows]);

  // Cmd/Ctrl+K always focuses the field, even mid-type in some other input --
  // the same choice GitHub's and Linear's own command palettes make. `/`
  // only when focus is not already inside something editable, so it still
  // types a literal slash in Mail's compose body or Chat's own composer.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const meta = event.metaKey || event.ctrlKey;
      if (meta && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        return;
      }
      if (event.key === '/' && !meta && !event.altKey) {
        const active = document.activeElement;
        const editable =
          active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement ||
          (active instanceof HTMLElement && active.isContentEditable);
        if (editable) return;
        event.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function setQuery(next: string) {
    controllerRef.current?.setQuery(next);
    setPanelOpen(Boolean(next.trim()));
    // Clearing the field returns a full-results view to its section's own
    // plain page (agora#269) -- it never touches any other page, since
    // `isResultsRoute` only ever matches the one this field's own `section`
    // already is.
    if (!next.trim() && isResultsRoute(parse(window.location.pathname), section)) {
      go(pathForSection(agoraIdRef.current, section));
    }
  }

  function openRow(row: SearchRow) {
    setPanelOpen(false);
    if (row.open) {
      // A row with `open` has nowhere to go yet -- a chat person, whose DM
      // may still need creating (`chat.ts`'s `chatPeopleRows`). The panel is
      // already closed above; a failure here (the DM never started) is
      // swallowed rather than sent anywhere, the same silent-degrade every
      // other search failure in this file gets. An answer that lands after
      // a company switch or sign-out is dropped, not followed.
      const openedIn = agoraIdRef.current;
      row
        .open()
        .then((route) => {
          if (mountedRef.current && agoraIdRef.current === openedIn) go(pathForRoute(route));
        })
        .catch(() => {});
      return;
    }
    go(pathForRoute(row.route));
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      if (flatRows.length === 0) return;
      event.preventDefault();
      setPanelOpen(true);
      setHighlighted((h) => (h === null ? 0 : Math.min(h + 1, flatRows.length - 1)));
    } else if (event.key === 'ArrowUp') {
      if (flatRows.length === 0) return;
      event.preventDefault();
      setPanelOpen(true);
      setHighlighted((h) => (h === null ? flatRows.length - 1 : Math.max(h - 1, 0)));
    } else if (event.key === 'Enter') {
      const picked = highlighted !== null ? flatRows[highlighted] : undefined;
      if (picked) {
        event.preventDefault();
        openRow(picked.row);
        return;
      }
      // Nothing highlighted (agora#216's 2026-09-21 comment, agora#269): a
      // full search in the current section, not the header panel's own
      // capped preview. Today has no results view of its own, so there Enter
      // opens the first result -- never nothing.
      const trimmedQuery = state.query.trim();
      if (!trimmedQuery) return;
      const route = fullSearchRoute(section, agoraIdRef.current, trimmedQuery);
      if (!route) {
        const first = flatRows[0];
        if (!first) return;
        event.preventDefault();
        openRow(first.row);
        return;
      }
      event.preventDefault();
      setPanelOpen(false);
      go(pathForRoute(route));
    } else if (event.key === 'Escape') {
      if (!panelOpen) {
        inputRef.current?.blur();
        return;
      }
      event.preventDefault();
      setPanelOpen(false);
    }
  }

  const placeholder = placeholderFor(section, companyName);
  const activeId =
    highlighted !== null && flatRows[highlighted] ? `search-option-${highlighted}` : undefined;
  const trimmed = state.query.trim();
  const showPanel = panelOpen && trimmed.length > 0;

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        type="text"
        className="shell-omni"
        block
        icon="search"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={panelId}
        aria-activedescendant={showPanel ? activeId : undefined}
        aria-autocomplete="list"
        aria-label={placeholder}
        placeholder={placeholder}
        autoComplete="off"
        value={state.query}
        trailing={state.query ? undefined : <Kbd>⌘K</Kbd>}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => setPanelOpen(trimmed.length > 0)}
        onBlur={() => setPanelOpen(false)}
        onKeyDown={onKeyDown}
        onClear={() => {
          setQuery('');
          inputRef.current?.focus();
        }}
      />
      {showPanel && (
        <div className="search-panel" id={panelId} role="listbox" aria-label="Search results">
          {state.loading && flatRows.length === 0 ? (
            <p className="m-0 p-3 text-muted text-sm">Searching…</p>
          ) : flatRows.length === 0 ? (
            <p className="m-0 p-3 text-muted text-sm">No results for “{trimmed}”.</p>
          ) : (
            state.groups.map((group) => (
              <div className="search-group" key={group.id}>
                <div className="search-group-title">{group.label}</div>
                {group.result.ok ? (
                  group.result.rows.map((row) => {
                    const flatIndex = indexOf.get(`${group.id}:${row.id}`);
                    if (flatIndex === undefined) return null;
                    return (
                      <SearchRowView
                        key={row.id}
                        row={row}
                        icon={GROUP_ICON[group.id] ?? 'search'}
                        highlighted={flatIndex === highlighted}
                        optionId={`search-option-${flatIndex}`}
                        onHover={() => setHighlighted(flatIndex)}
                        onOpen={() => openRow(row)}
                      />
                    );
                  })
                ) : (
                  <p className="m-0 py-2 px-3 text-bad text-xs">{group.result.error}</p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SearchRowView({
  row,
  icon,
  highlighted,
  optionId,
  onHover,
  onOpen,
}: {
  row: SearchRow;
  icon: IconName;
  highlighted: boolean;
  optionId: string;
  onHover: () => void;
  onOpen: () => void;
}) {
  return (
    // Virtual focus, exactly `@wtfalch/design`'s own `Command` doc describes
    // for this same field-over-listbox pattern: real DOM focus stays on the
    // `<input>` (`onMouseDown` below stops a click from ever moving it), and
    // `aria-activedescendant` is how a screen reader learns which option is
    // current. `tabIndex={-1}` satisfies biome's own `useFocusableInteractive`
    // without joining the tab order; `onKeyDown` satisfies
    // `useKeyWithClickEvents` for the one path that could reach this element
    // with a real key event despite the field owning Enter globally.
    <div
      id={optionId}
      role="option"
      tabIndex={-1}
      aria-selected={highlighted}
      className={highlighted ? 'search-row on' : 'search-row'}
      onMouseDown={(event) => event.preventDefault()}
      onMouseEnter={onHover}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onOpen();
      }}
    >
      <span className="flex flex-none text-muted" aria-hidden="true">
        <Icon name={icon} size={16} />
      </span>
      <span className="flex flex-1 flex-col min-w-0 gap-0.5">
        <span className="truncate text-text text-sm font-strong">{row.title}</span>
        {row.snippet && <span className="truncate text-muted text-xs">{row.snippet}</span>}
      </span>
      {row.time && (
        <span className="flex-none text-muted text-2xs whitespace-nowrap">
          {row.dated ? formatMessageWhen(row.time) : formatWhen(row.time)}
        </span>
      )}
    </div>
  );
}
